import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { RiArrowDownSLine } from "react-icons/ri";
import searchIcon from "../../../../assets/admin/search.svg";

const ProposalProvinceField = ({
    selectedProvince = "",
    onChange,
    provincesList = [],
    disabled = false,
    error = "",
}) => {
    const [showOption, setShowOption] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    // Smooth focus without causing page jump/scroll glitch
    useEffect(() => {
        if (showOption && searchInputRef.current) {
            searchInputRef.current.focus({ preventScroll: true });
        }
    }, [showOption]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowOption(false);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, []);

    // Find if current selected value matches a province
    const matchedProvince = (provincesList || []).find((item) => {
        if (!selectedProvince || !item) return false;
        const normSelected = String(selectedProvince).trim().toLowerCase();
        const normCode = String(item.code || "").trim().toLowerCase();
        const normName = String(item.name || "").trim().toLowerCase();
        return (
            (normCode && normSelected === normCode) ||
            (normName && normSelected === normName) ||
            (normName && normCode && normSelected === `${normName} (${normCode})`) ||
            (normName && normSelected.startsWith(`${normName} `)) ||
            (normCode && normSelected.includes(`(${normCode})`))
        );
    });

    // Display text in button: matched name, raw selected value, or placeholder
    const displayText = matchedProvince
        ? matchedProvince.name
        : (selectedProvince || "Provincia");

    // Filter provinces based on search query
    const filteredProvinceList = (provincesList || []).filter((item) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (
            item?.name?.toLowerCase().includes(q) ||
            item?.code?.toLowerCase().includes(q)
        );
    });

    const isOptionSelected = (option) => {
        if (!selectedProvince || !option) return false;
        if (matchedProvince) {
            return (
                Boolean(matchedProvince.code && matchedProvince.code === option.code) ||
                Boolean(matchedProvince.id && matchedProvince.id === option.id) ||
                Boolean(matchedProvince.name && matchedProvince.name === option.name)
            );
        }
        return (
            Boolean(option.code && selectedProvince === option.code) ||
            Boolean(option.name && selectedProvince === option.name)
        );
    };

    const handleProvinceSelection = (provinceCode) => {
        const currentlySelected = isOptionSelected({ code: provinceCode });
        const nextValue = currentlySelected ? "" : provinceCode;

        if (onChange) {
            onChange({
                target: {
                    name: "destination_province",
                    value: nextValue,
                },
            });
        }
    };

    return (
        <div
            className={`flex flex-col gap-1.5 w-full ${
                showOption ? "relative z-50" : "relative z-10"
            }`}
            ref={dropdownRef}
        >
            <label className="text-xs font-semibold poppins-medium text-[#4A4A4A]">
                Provincia <span className="text-red-500">*</span>
            </label>
            <div className="relative group w-full">
                {/* Trigger button: keeps text and icon in identical, fixed position with zero movement */}
                <button
                    type="button"
                    disabled={disabled}
                    className={`${
                        disabled
                            ? "rounded-3xl border-gray-200 bg-[#F8F8F8] text-[#555] cursor-not-allowed select-none"
                            : error
                            ? "rounded-3xl border-red-500 bg-red-50/20 text-[#303030] focus:border-red-500"
                            : showOption
                            ? "border-b-0 rounded-t-[21px] rounded-b-none border-[#d5d6d9] shadow-lg bg-white"
                            : "rounded-3xl border-gray-300 bg-white"
                    } poppins-regular border inline-flex justify-between items-center w-full px-4 h-[42px] outline-none text-xs relative z-50 focus:outline-none focus:border-[#d5d6d9]`}
                    onClick={() => {
                        if (disabled) return;
                        setShowOption((prev) => !prev);
                        setSearchQuery("");
                    }}
                >
                    <span
                        className={`mr-2 flex items-center gap-3 truncate ${
                            matchedProvince || selectedProvince
                                ? (disabled ? "text-[#555]" : "text-[#303030]")
                                : "text-[#8B8B8B]"
                        }`}
                    >
                        {displayText}
                    </span>

                    <RiArrowDownSLine
                        className={`transform transition-transform duration-300 shrink-0 ${
                            showOption ? "rotate-180" : ""
                        } ${disabled ? "opacity-40" : ""}`}
                        size={20}
                        color="#9f414f"
                    />
                </button>

                {/* Dropdown panel directly connected below the button to form a seamless rounded card */}
                {showOption && (
                    <div
                        className="bg-white border border-[#d5d6d9] border-t-0 absolute top-[41px] left-0 right-0 z-50 p-3 pt-1.5 rounded-b-[21px] shadow-lg space-y-2.5 w-full"
                    >
                        {/* Search pill input matching exact screenshot design */}
                        <div className="flex items-center px-3 py-1.5 border border-gray-200 rounded-full bg-white gap-2.5 shadow-2xs">
                            <img
                                src={searchIcon}
                                className="w-4 h-4 opacity-50 shrink-0"
                                alt="Search"
                            />
                            <input
                                ref={searchInputRef}
                                type="text"
                                className="w-full bg-transparent outline-none poppins-regular text-xs text-[#303030] placeholder:text-xs placeholder-[#8F9CAE]"
                                placeholder="Provincia"
                                value={searchQuery || ""}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>

                        {/* Checkbox list */}
                        <div className="max-h-[140px] overflow-y-auto btn-all-yscroll space-y-1 pt-0.5">
                            {filteredProvinceList && filteredProvinceList.length > 0 ? (
                                filteredProvinceList.map((option, idx) => (
                                    <div
                                        className="flex items-center gap-3 cursor-pointer py-1 px-1 hover:bg-[#FAF4F5]/60 rounded-md transition-colors"
                                        key={option?.id || option?.code || idx}
                                        onClick={() =>
                                            handleProvinceSelection(
                                                option?.code || option?.name
                                            )
                                        }
                                    >
                                        <label
                                            className="cursor-pointer flex items-center filter_list_btn"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <input
                                                type="checkbox"
                                                className="outline-none cursor-pointer"
                                                checked={isOptionSelected(option)}
                                                onChange={() =>
                                                    handleProvinceSelection(
                                                        option?.code || option?.name
                                                    )
                                                }
                                            />
                                        </label>
                                        <span className="text-xs text-[#6B6B6B] poppins-regular select-none">
                                            {option?.name || option?.code || ""}
                                        </span>
                                    </div>
                                ))
                            ) : (
                                <div className="flex items-center justify-center p-4">
                                    <span className="text-xs text-[#8B8B8B] poppins-regular">
                                        Nessuna opzione
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
            {!disabled && error && (
                <span className="text-[11px] text-red-500 poppins-regular px-1 mt-0.5">
                    {error}
                </span>
            )}
        </div>
    );
};

ProposalProvinceField.propTypes = {
    selectedProvince: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    provincesList: PropTypes.array,
    disabled: PropTypes.bool,
    error: PropTypes.string,
};

export default ProposalProvinceField;
